


import MaterialReactTable, {
  MRT_FullScreenToggleButton,
  MRT_ShowHideColumnsButton,
  MRT_ToggleDensePaddingButton,
  MRT_ToggleFiltersButton,
} from "material-react-table";
import { Delete as DeleteIcon } from "@mui/icons-material/";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { Box, IconButton, Tooltip, Typography } from "@mui/material";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import DefaultSnackbar from "components/DefaultSnackbar";
import ConfirmDeleteDialog from "components/formModals/ConfirmDeleteDialog";
import GlobalSearch from "components/GlobalSearch";
import StyledToolbarButton from "components/styled-components/StyledToolbarButton";
import { download, generateCsv, mkConfig } from "export-to-csv";
import { StyledInnerToolbarWrapper, StyledToolbarWrapper } from "./Styles";
import TableActionsMenu from "./TableActionsMenu";
import { useModal } from "contexts/modalContext";
import { FaIdBadge } from "react-icons/fa";
import AttendeeBadgeModal from "components/formModals/Badge/AttendeeBadgeModal";
import StyledToolbarMenu from "components/styled-components/StyledToolbarMenu";
import {
  useGetInvitationTypesQuery,
  useUpdateBulkInvitationTypeMutation,
} from "features/invitations/invitationTypeSlice";

export default function EnhancedDataTable({
  rows,
  sendEmail,
  headCells,
  onDelete,
  onEdit,
  onView,
  onSelect,
  isRowDisabled,
  isEvent = false,
  editIcon = true,
  deleteIcon = true,
  showIcon = true,
  actions = [],
  isLoading = false,
  manualPagination = false,
  pagination,
  onPaginationChange,
  rowCount = null,
  toolbarActions = null,
  isContatct = false,
  allowExport = true,
  additionalExportActions = null,
  onDeleteSelected,
  editTooltip = null,
  deleteTooltip = null,
  viewToolTip = null,
  toolbarInternalActions = [],
  editDisabled,
  deleteDisabled,
  allowMassDelete = true,
  columnPinning = {},
  enableRowSelection = true,
  enableRowNumbers = true, // Kept for backward compatibility, but unused
  searchProps = null,
  event = null,
  tableType = "",
  invitationTypes,
}) {
  const rowActions = useMemo(() => {
    return ({ row }) => {
      return (
        <TableActionsMenu
    sendEmail={sendEmail}
          row={row}
          actions={actions}
          isRowDisabled={isRowDisabled}
          showIcon={showIcon}
          editIcon={editIcon}
          deleteIcon={deleteIcon}
          onView={onView}
          onEdit={onEdit}
          onDelete={onDelete}
          viewTooltip={viewToolTip}
          editTooltip={editTooltip}
          deleteTooltip={deleteTooltip}
          isEditDisabled={isEditDisabled}
          isDeleteDisabled={isDeleteDisabled}
        />
      );
    };
  }, [
    actions,
    deleteIcon,
    editIcon,
    showIcon,
    onDelete,
    onEdit,
    onView,
    isRowDisabled,
    viewToolTip,
    editTooltip,
    deleteTooltip,
  ]);

  const [rowSelection, setRowSelection] = useState({});
  const [deleteSelected, setDeleteSelected] = useState(false);
  const [snackOptions, setSnackOptions] = useState({ open: false, text: "" });
  const [isPageLoading, setIsPageLoading] = useState(false);

  const noActionTabTables = [
    "attendeeBadgeList",
    "transactionHistoryList",
    "pushNotificationTable",
  ];
  const noToolbarActionsTables = ["attendeeBadgeList", "transactionHistoryList"];

  const [defaultPagination, setDefaultPagination] = useState({
    pageIndex: 0,
    pageSize: 25,
  });

  const columns = useMemo(() => {
    const baseColumns = headCells.map(({ label, id, ...item }) => ({
      header: label,
      accessorKey: id,
      ...item,
    }));

    // Add sequential row number column with "#" header
    return [
      {
        header: "#",
        accessorKey: "rowNumber",
        id: "rowNumber",
        size: 50,
        enableSorting: false,
        enableColumnActions: false,
        Cell: ({ row, table }) => {
          const pageIndex = manualPagination
            ? pagination.pageIndex
            : defaultPagination.pageIndex;
          const pageSize = manualPagination
            ? pagination.pageSize
            : defaultPagination.pageSize;
          const rowIndex = table.getRowModel().rows.indexOf(row);
          const rowNumber = pageIndex * pageSize + rowIndex + 1;
          return (
            <Typography variant="body2" sx={{ px: 1 }}>
              {rowNumber}
            </Typography>
          );
        },
      },
      ...baseColumns,
    ];
  }, [headCells, manualPagination, pagination, defaultPagination]);

  const filteredRowIds = useMemo(() => {
    if (isRowDisabled) {
      return rows.filter((row) => !isRowDisabled(row)).map((row) => row.id);
    }
    return rows.map((row) => row.id);
  }, [rows, isRowDisabled]);

  const onRowSelectionChange = (e) => {
    let r = e(rowSelection);

    if (isRowDisabled) {
      Object.keys(r).forEach((key) => {
        if (!filteredRowIds.includes(parseInt(key))) delete r[key];
      });
    }

    const isAllSelected =
      Object.keys(rowSelection).length === filteredRowIds.length &&
      Object.keys(rowSelection).length === Object.keys(r).length;
    if (isAllSelected) {
      setRowSelection({});
      return;
    }

    setRowSelection(r);
  };

  const numberOfSelectedRows = Object.keys(rowSelection).map((i) => Number(i))
    .length;

  // Use a ref to track the previous selection to prevent infinite loops
  const prevSelectedIdsRef = useRef([]);
  useEffect(() => {
    if (onSelect) {
      const currentSelectedIds = Object.keys(rowSelection).map((i) => Number(i));
      // Only call onSelect if the selection has changed
      if (
        JSON.stringify(currentSelectedIds) !==
        JSON.stringify(prevSelectedIdsRef.current)
      ) {
        onSelect(currentSelectedIds);
        prevSelectedIdsRef.current = currentSelectedIds;
      }
    }
  }, [rowSelection, onSelect]);

  const handleClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackOptions({
      open: false,
    });
  };

  const [enableRowActions, setEnableRowActions] = useState(true);
  useEffect(() => {
    setEnableRowActions(showIcon || deleteIcon || editIcon || actions.length > 0);
  }, [showIcon, deleteIcon, editIcon, actions]);

  const csvConfig = useMemo(() => {
    const csvOptions = {
      useKeysAsHeaders: false,
      columnHeaders: columns.map((c) => c.header),
    };
    return mkConfig(csvOptions);
  }, [columns]);

const handleExportRows = useCallback(
  (selectedRows) => {
    const csvData = selectedRows.map((row, index) => {
      const rawRow = row.original; 
      const info = {};

      columns.forEach((col) => {
        if (col.header === "#") {
          const pageIndex = manualPagination ? pagination.pageIndex : defaultPagination.pageIndex;
          const pageSize = manualPagination ? pagination.pageSize : defaultPagination.pageSize;
          info[col.header] = pageIndex * pageSize + index + 1;

        } else if (col.header === "Event Schedule" && rawRow.event_schedule) {
          info[col.header] =
            `Date: ${rawRow.event_schedule.start_date} ` +
            `Start: ${rawRow.event_schedule.start_time} ` +
            `End: ${rawRow.event_schedule.end_time}`;

        } else if (col.header === "Sessions" && rawRow.event_session) {
          info[col.header] = rawRow.event_session.name;

        } 
        else {
          let value = rawRow[col.accessorKey];

          if (col.header === "Photo") {
            value = "Image"; // or rawRow.Photo if you want JSON string
          }

          if (Array.isArray(value)) {
            info[col.header] = value.join(",");
          } else if (value && typeof value === "object") {
            info[col.header] = JSON.stringify(value);
          } else {
            info[col.header] = value ?? "";
          }
        }
      });

      return info;
    });

    const csv = generateCsv(csvConfig)(csvData);
    download(csvConfig)(csv);
    setSnackOptions({
      open: true,
      text: "Successfully exported selected table data",
    });
  },
  [columns, csvConfig, manualPagination, pagination, defaultPagination]
);

const handleExportData = useCallback(() => {
  const csvData = rows.map((row, index) => {
    const info = {};

    columns.forEach((col) => {
      if (col.header === "#") {
        const pageIndex = manualPagination ? pagination.pageIndex : defaultPagination.pageIndex;
        const pageSize = manualPagination ? pagination.pageSize : defaultPagination.pageSize;
        info[col.header] = pageIndex * pageSize + index + 1;

      } else if (col.header === "Event Schedule" && row[col.accessorKey]) {
        // already flattened schedule object
        const sched = row[col.accessorKey];
        info[col.header] =
          typeof sched === "object"
            ? `Date: ${sched.start_date} Start: ${sched.start_time} End: ${sched.end_time}`
            : String(sched);

      } 
     else if (col.header === "Sessions") {
  const sessionData = row.event_session;

  if (Array.isArray(sessionData)) {
    info[col.header] = sessionData.map(s => s.name).join(", ");
  } else if (sessionData && typeof sessionData === "object") {
    info[col.header] = sessionData.name || "";
  } else {
    info[col.header] = row[col.accessorKey] ?? "";
  }
}

      else {
        let value = col.header === "Photo" ? "Image" : row[col.accessorKey];

        if (Array.isArray(value)) {
          info[col.header] = value.join(",");
        } else if (value && typeof value === "object") {
          info[col.header] = JSON.stringify(value);
        } else {
          info[col.header] = value ?? "";
        }
      }
    });

    return info;
  });

  const csv = generateCsv(csvConfig)(csvData);
  download(csvConfig)(csv);
  setSnackOptions({
    open: true,
    text: "Successfully exported table data",
  });
}, [rows, columns, csvConfig, manualPagination, pagination, defaultPagination]);


  const handleDeleteSelection = async (password) => {
    if (onDeleteSelected)
      await onDeleteSelected(
        Object.keys(rowSelection).map((i) => Number(i)),
        password
      );
    setRowSelection({});
  };

  const isEditDisabled = (row) => {
    if (isRowDisabled !== undefined && editDisabled !== undefined) {
      return isRowDisabled(row) || editDisabled(row);
    }
    if (isRowDisabled !== undefined) return isRowDisabled(row);
    if (editDisabled !== undefined) return editDisabled(row);
    return false;
  };

  const isDeleteDisabled = (row) => {
    if (isRowDisabled !== undefined && deleteDisabled !== undefined) {
      return isRowDisabled(row) || deleteDisabled(row);
    }
    if (isRowDisabled !== undefined) return isRowDisabled(row);
    if (deleteDisabled !== undefined) return deleteDisabled(row);
    return false;
  };

  const { showModal, toggleModal } = useModal();
  const isTableAttendeeBadge = noActionTabTables.includes(tableType);
  const isNoToolbarActionsTable = noToolbarActionsTables.includes(tableType);

  const [bulkUpdateInvitationTypes, { isLoading: isSendLoading }] =
    useUpdateBulkInvitationTypeMutation();

  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleClick = (event) => {
    if (invitationTypes?.data.length)
      setAnchorEl(event.currentTarget);
    else {
      setSnackOptions({
        text: "No invitation types added",
        open: true,
        severity: "error",
      });
    }
  };

  const handleClosing = (selection) => {
    setAnchorEl(null);
    if (typeof selection === "number") {
      bulkUpdateInvitationTypes({
        invitation_ids: Object.keys(rowSelection).map((row) => parseInt(row)),
        invitation_type_id: selection,
      })
        .then(() => {
          setSnackOptions({
            text: "Successfully modified invitation types",
            open: true,
            severity: "success",
          });
          window.location.reload();
        })
        .catch((error) => {
          setSnackOptions({
            text: "Failed to modify invitation types",
            open: true,
            severity: "error",
          });
        });
    }
  };

  const handlePaginationChange = useCallback(
    (updater) => {
      if (manualPagination) {
        setIsPageLoading(true);
        onPaginationChange(updater);
      } else {
        setDefaultPagination(updater);
      }
    },
    [manualPagination, onPaginationChange]
  );

  useEffect(() => {
    setIsPageLoading(false);
  }, [rows, isLoading]);

  return (
    <Box>
      <MaterialReactTable
        columns={columns}
        data={rows}
        enableRowSelection={enableRowSelection}
        enableRowActions={!isTableAttendeeBadge ? enableRowActions : null}
        enableSubRowSelection={true}
        manualPagination={manualPagination}
        enablePinning
        state={{
          isLoading: isLoading || isPageLoading,
          rowSelection,
          pagination: manualPagination ? pagination : defaultPagination,
        }}
        initialState={{
          density: "compact",
          columnPinning,
        }}
        onPaginationChange={handlePaginationChange}
        rowCount={manualPagination ? rowCount : null}
        enableDensityToggle={true}
        getRowId={(originalRow) => originalRow.id}
        onRowSelectionChange={onRowSelectionChange}
        renderRowActions={rowActions}
        positionActionsColumn="last"
        muiTablePaginationProps={{
          rowsPerPageOptions: [5, 10, 15, 20, 25, 30, 50, 100, 200],
        }}
        muiSelectCheckboxProps={({ row }) => ({
          disabled: isRowDisabled ? isRowDisabled(row.original) : false,
        })}
        muiTableBodyRowProps={({ row, table }) => ({
          onClick: (e) => {
            if (e.target.attributes[0]?.nodeValue === "visible") {
              return;
            }
            if (
              e.target?.id !== "row-menu" &&
              e.target?.id !== "demo-customized-button" &&
              e.target?.parentNode?.id !== "demo-customized-menu" &&
              e.target.nodeName !== "svg" &&
              e.target.nodeName !== "IMG"
            ) {
              if (onEdit && !isEvent)
                return onEdit(row.original.id, row.original);
              if (onView && isEvent)
                return onView(row.original.id, row.original);
              return () =>
                setSnackOptions({
                  text: "No detail page for this row",
                  open: true,
                  severity: "info",
                });
            }
            return () => {};
          },
          sx: {
            "&:hover > *": { cursor: "pointer" },
          },
        })}
        renderTopToolbarCustomActions={({ table }) => {
          if (!isNoToolbarActionsTable) {
            return (
              <StyledToolbarWrapper>
                {toolbarActions}
                {allowExport && (
                  <Box
                    sx={{
                      display: "flex",
                      gap: "1rem",
                      p: "0.5rem",
                      flexWrap: "wrap",
                    }}
                  >
                    {!isContatct && (
                      <StyledToolbarButton
                        onClick={handleExportData}
                        startIcon={<FileDownloadIcon />}
                      >
                        <Typography variant="p">Export All Data</Typography>
                      </StyledToolbarButton>
                    )}
                    <StyledToolbarButton
                      disabled={
                        !table.getIsSomeRowsSelected() &&
                        !table.getIsAllRowsSelected()
                      }
                      onClick={() =>
                        handleExportRows(table.getSelectedRowModel().rows)
                      }
                      startIcon={<FileDownloadIcon />}
                    >
                      <Typography variant="p">Export Selected Rows</Typography>
                    </StyledToolbarButton>
                    {additionalExportActions}
                  </Box>
                )}
              </StyledToolbarWrapper>
            );
          }
          return null;
        }}
        renderToolbarInternalActions={({ table }) => {
          if (!isTableAttendeeBadge) {
            return (
              <StyledInnerToolbarWrapper>
                {toolbarInternalActions.map((action) => action)}
                {numberOfSelectedRows > 0 && allowMassDelete && (
                  <Tooltip title="Delete Selected Rows">
                    <span>
                      <IconButton
                        onClick={() => {
                          setDeleteSelected(true);
                        }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                )}
                {searchProps && <GlobalSearch {...searchProps} />}
                <MRT_ToggleFiltersButton table={table} />
                <MRT_ShowHideColumnsButton table={table} />
                <MRT_ToggleDensePaddingButton table={table} />
                <MRT_FullScreenToggleButton table={table} />
              </StyledInnerToolbarWrapper>
            );
          }
          if (tableType === "attendeeBadgeList") {
            return (
              <StyledInnerToolbarWrapper>
                {toolbarInternalActions.map((action) => action)}
                {numberOfSelectedRows > 0 && (
                  <>
                    <StyledToolbarMenu
                      disabled={Object.keys(rowSelection).length < 1 || isSendLoading}
                      anchorEl={anchorEl}
                      open={open}
                      handleClose={handleClosing}
                      handleClick={handleClick}
                      options={invitationTypes?.data?.map((invitation) => ({
                        id: invitation.id,
                        name: invitation.name,
                      }))}
                    />
                    <StyledToolbarButton
                      startIcon={<FaIdBadge />}
                      sx={{ width: "145px", marginLeft: "2rem" }}
                      onClick={() => {
                        const selectedRowsModel = table.getSelectedRowModel();
                        const rowValue = selectedRowsModel.rows.map(
                          (row) => row.original
                        );
                        toggleModal("modal5", { rowValue });
                     
                      }}
                    >
                      <Typography variant="p">Print Badge</Typography>
                    </StyledToolbarButton>
                  </>
                )}
                {searchProps && <GlobalSearch {...searchProps} />}
              </StyledInnerToolbarWrapper>
            );
          }
        }}
      />

      {deleteSelected && (
        <ConfirmDeleteDialog
          open={deleteSelected}
          onClose={() => setDeleteSelected(false)}
          action={handleDeleteSelection}
          text={`Delete ${numberOfSelectedRows} items`}
        />
      )}
      {showModal.modal5 && <AttendeeBadgeModal event={event} />}
      <DefaultSnackbar options={snackOptions} handleClose={handleClose} />
    </Box>
  );
}
